/**
 * Playmaker — Unified Wedge component.
 * One component renders all 11 integration wedges with consistent anatomy:
 *   [Header: logo · provider name · last-synced · expand toggle]
 *   [Body:   provider-specific content]
 *   [Footer: primary action · secondary action]
 *
 * State branches: connected_fresh | connected_stale | not_connected | not_linked | loading | error
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { fmtMoney, formatMarketValue } from "../../lib/format";
import {
  ChevronDown, ChevronUp, ExternalLink, RefreshCw, AlertCircle, Lock,
  TrendingUp, TrendingDown, Calendar as CalendarIcon, Bell, FileSignature,
  Scale, Mail, MessageCircle, Plane, Hotel, Database, Activity, Sparkles,
} from "lucide-react";

const PROVIDER_HUE = {
  wyscout: "bg-purple-100 text-purple-800 border-purple-200",
  scisports: "bg-rose-100 text-rose-800 border-rose-200",
  transfermarkt: "bg-blue-100 text-blue-800 border-blue-200",
  transferroom: "bg-amber-100 text-amber-800 border-amber-200",
  api_football: "bg-emerald-100 text-emerald-800 border-emerald-200",
  contract: "bg-slate-100 text-slate-800 border-slate-200",
  legal: "bg-orange-100 text-orange-800 border-orange-200",
  email: "bg-red-100 text-red-800 border-red-200",
  whatsapp_sms: "bg-teal-100 text-teal-800 border-teal-200",
};

const PROVIDER_ICON = {
  wyscout: Database, scisports: Sparkles, transfermarkt: TrendingUp,
  transferroom: Sparkles, api_football: CalendarIcon,
  contract: FileSignature, legal: Scale, email: Mail, whatsapp_sms: MessageCircle,
};

const PERC_TONE = {
  green: "bg-emerald-500",
  blue: "bg-blue-500",
  amber: "bg-amber-500",
  gray: "bg-slate-400",
};

export function Wedge({ provider, clientId, onAffiliateClick, onExpand }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Collapse state — persisted per (provider, client_id) so it survives reloads/per-client.
  const lsKey = `wedge_collapsed_${provider}_${clientId}`;
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(lsKey) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(lsKey, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed, lsKey]);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const fetch = (opts = {}) => {
    if (opts.refresh) setRefreshing(true); else setLoading(true);
    setError(null);
    return api.get(`/wedges/${provider}/${clientId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Couldn't load"))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [provider, clientId]);

  const Icon = PROVIDER_ICON[provider] || Database;
  const hue = PROVIDER_HUE[provider] || "bg-slate-100 text-slate-800 border-slate-200";

  if (loading && !data) {
    return (
      <div className="pm-card p-4" data-testid={`wedge-${provider}-loading`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-3 w-32 bg-slate-100 rounded animate-pulse" />
        </div>
        <div className="h-12 bg-slate-50 rounded animate-pulse" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="pm-card p-4 border-red-200" data-testid={`wedge-${provider}-error`}>
        <div className="text-sm text-red-700 flex items-center gap-1.5"><AlertCircle size={12} /> {error}</div>
      </div>
    );
  }
  if (!data) return null;

  const isNotConnected = data.state === "not_connected";
  const isNotLinked = data.state === "not_linked";
  const isStale = data.state === "connected_stale";

  const handleAction = (action) => {
    if (!action) return;
    if (action.startsWith("expand:")) {
      const target = action.split(":")[1];
      if (onExpand) onExpand(target);
      return;
    }
    if (action.startsWith("navigate:")) {
      const target = action.split(":")[1];
      const routes = {
        contracts: `/clients/${clientId}?tab=contracts`,
        legal: `/legal?client_id=${clientId}`,
        inbox: `/inbox?client_id=${clientId}`,
        schedule: `/clients/${clientId}?tab=schedule`,
        new_case: `/cases?new=1&client_id=${clientId}`,
        new_contract: `/clients/${clientId}?tab=contracts&new=1`,
      };
      if (routes[target]) navigate(routes[target]);
    } else if (action === "refresh:transfermarkt") {
      fetch({ refresh: true });
    } else if (action === "sync:api_football") {
      // Trigger live sync for this client, then refetch the wedge.
      setRefreshing(true);
      api.post("/integrations/api-football/sync-fixtures", { client_id: clientId })
        .then((r) => {
          if (r.data?.account_error) {
            setError(r.data.account_error);
          }
        })
        .catch((e) => setError(e.response?.data?.detail || "Sync failed"))
        .finally(() => {
          setRefreshing(false);
          fetch({ refresh: true });
        });
    }
  };

  return (
    <div className="pm-card overflow-hidden" data-testid={`wedge-${provider}`}>
      {/* HEADER */}
      <div className="px-4 py-3 flex items-center gap-2 border-b border-slate-100">
        <div className={`w-8 h-8 rounded-lg ${hue} border flex items-center justify-center shrink-0 text-[11px] font-display font-black`}>
          {data.header?.logo_short || <Icon size={14} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm tracking-tight truncate flex items-center gap-1.5">
            {data.header?.name}
            {data.header?.active_count > 0 && <span className="pm-pill pm-pill-amber text-[10px]" data-testid={`wedge-${provider}-count`}>{data.header.active_count} active</span>}
            {data.header?.unread > 0 && <span className="pm-pill pm-pill-red text-[10px]" data-testid={`wedge-${provider}-unread`}>{data.header.unread} unread</span>}
          </div>
        </div>
        {data.header?.last_synced_label && (
          <span className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isStale ? "text-amber-700" : "text-slate-400"}`} data-testid={`wedge-${provider}-synced`}>
            {isStale && "Stale · "}{data.header.last_synced_label}
          </span>
        )}
        <button
          onClick={() => fetch({ refresh: true })}
          disabled={refreshing}
          className={`p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700 ${refreshing ? "animate-spin" : ""}`}
          data-testid={`wedge-${provider}-refresh`}
          title="Refresh"
        >
          <RefreshCw size={11} />
        </button>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700"
          data-testid={`wedge-${provider}-toggle`}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand wedge" : "Collapse wedge"}
          title={collapsed ? "Expand" : "Collapse"}
        >
          <ChevronDown
            size={14}
            className="transition-transform duration-200"
            style={{ transform: collapsed ? "rotate(0deg)" : "rotate(180deg)" }}
          />
        </button>
      </div>

      {/* BODY */}
      {!collapsed && (
        <div className="p-4" data-testid={`wedge-${provider}-body`}>
          {isNotConnected || isNotLinked ? (
            <ConnectPrompt data={data} provider={provider} />
          ) : (
            <ProviderBody provider={provider} body={data.body} />
          )}
        </div>
      )}

      {/* FOOTER */}
      {!collapsed && data.footer && (
        <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/50 flex items-center gap-2 text-[12px]">
          {data.footer.primary_url ? (
            <a href={data.footer.primary_url} target={data.footer.primary_url.startsWith("/") ? "_self" : "_blank"} rel="noopener"
               className="font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
               data-testid={`wedge-${provider}-primary`}
            >
              {data.footer.primary_label} <ExternalLink size={11} />
            </a>
          ) : (
            <button
              onClick={() => handleAction(data.footer.primary_action)}
              className="font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
              data-testid={`wedge-${provider}-primary`}
            >
              {data.footer.primary_label} →
            </button>
          )}
          <div className="flex-1" />
          {data.footer.affiliate_url ? (
            <a
              href={data.footer.affiliate_url} target="_blank" rel="noopener"
              onClick={() => onAffiliateClick && onAffiliateClick(provider)}
              className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold inline-flex items-center gap-1"
              data-testid={`wedge-${provider}-affiliate`}
            >
              {data.footer.secondary_label} <ExternalLink size={10} />
            </a>
          ) : data.footer.secondary_label ? (
            <button
              onClick={() => handleAction(data.footer.secondary_action)}
              className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold"
              data-testid={`wedge-${provider}-secondary`}
            >
              {data.footer.secondary_label}
            </button>
          ) : null}
        </div>
      )}
      {(isNotConnected) && data.footer?.affiliate_url && (
        <div className="px-4 py-1 text-[10px] text-slate-400 leading-tight" data-testid={`wedge-${provider}-disclosure`}>
          We may earn a referral fee if you sign up via this link. This does not affect your price.
        </div>
      )}
    </div>
  );
}

function ConnectPrompt({ data, provider }) {
  const Icon = PROVIDER_ICON[provider] || Database;
  return (
    <div className="flex items-start gap-3 py-1">
      <div className="w-10 h-10 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center shrink-0">
        <Icon size={18} />
      </div>
      <div className="flex-1 text-[13px] text-slate-700 leading-relaxed">
        {data.body?.prompt || "Connect this integration to start pulling data."}
      </div>
    </div>
  );
}

/* -------------------- per-provider body renderers -------------------- */

function ProviderBody({ provider, body }) {
  if (!body) return null;
  switch (provider) {
    case "transfermarkt": return <TransfermarktBody body={body} />;
    case "wyscout": return <WyscoutBody body={body} />;
    case "scisports": return <SciSportsBody body={body} />;
    case "transferroom": return <TransferRoomBody body={body} />;
    case "api_football": return <ApiFootballBody body={body} />;
    case "contract": return <ContractBody body={body} />;
    case "legal": return <LegalBody body={body} />;
    case "email":
    case "whatsapp_sms": return <InboxBody body={body} channel={provider} />;
    default: return null;
  }
}

function TransfermarktBody({ body }) {
  const trendUp = (body.value_change_eur || 0) > 0;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-3">
        <div className="font-display text-3xl font-black tracking-tight" data-testid="tm-value">{formatMarketValue(body.market_value_eur)}</div>
        {body.value_change_eur ? (
          <span className={`text-[12px] font-bold ${trendUp ? "text-emerald-700" : "text-red-700"} inline-flex items-center gap-0.5`}>
            {trendUp ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {formatMarketValue(Math.abs(body.value_change_eur))} since Jan
          </span>
        ) : null}
      </div>
      <div className="text-[11px] text-slate-500">Peak: {formatMarketValue(body.peak_value_eur)}</div>
      {body.history_sparkline?.length > 1 && (
        <Sparkline points={body.history_sparkline} trendUp={trendUp} />
      )}
      <div className="text-[12px] text-slate-700">
        Contract: <b>{body.current_club}</b>{" "}
        {body.contract_expiry && <>· Expires <span className="text-red-700 font-bold">{body.contract_expiry}</span></>}
      </div>
      {body.last_transfer?.from && (
        <div className="text-[12px] text-slate-700">
          Last transfer: <b>{body.current_club}</b> ← {body.last_transfer.from} · {body.last_transfer.year || ""} · {body.last_transfer.fee_eur ? fmtMoney(body.last_transfer.fee_eur) : "free"}
        </div>
      )}
      {body.stub && <span className="pm-pill pm-pill-amber text-[10px]">Stub data — paste APIFY_TOKEN for live</span>}
    </div>
  );
}

function Sparkline({ points, trendUp }) {
  const w = 200, h = 40;
  const max = Math.max(...points), min = Math.min(...points);
  const range = (max - min) || 1;
  const stride = w / (points.length - 1);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${i * stride},${h - ((p - min) / range) * h}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="w-full h-10" data-testid="tm-sparkline">
      <path d={path} fill="none" strokeWidth="2" stroke={trendUp ? "#059669" : "#dc2626"} />
    </svg>
  );
}

function WyscoutBody({ body }) {
  const s = body.stats || {};
  const PILL = ({ label, value, hi }) => (
    <div className={`px-2 py-1.5 rounded-lg border ${hi ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-white"}`}>
      <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-slate-400">{label}</div>
      <div className="font-display text-sm font-black tracking-tight">{value}</div>
    </div>
  );
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-1.5">
        <PILL label="Appearances" value={s.appearances} />
        <PILL label="Goals" value={s.goals} hi />
        <PILL label="xG" value={s.xg} hi />
        <PILL label="Key passes/90" value={s.key_passes_p90} />
        <PILL label="Duels won %" value={`${s.duels_won_pct}%`} />
        <PILL label="Prog. carries/90" value={s.progressive_carries_p90} />
      </div>
      {s.latest_match && (
        <div className="text-[11px] text-slate-700 border-t pt-2 border-slate-100">
          {s.latest_match.label} · {s.latest_match.minutes}' · {s.latest_match.goals}G · {s.latest_match.assists}A · ★ <b>{s.latest_match.rating}</b>
        </div>
      )}
      {body.stub && <span className="pm-pill pm-pill-amber text-[10px]">Stub data · connect real Wyscout for live</span>}
    </div>
  );
}

function SciSportsBody({ body }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-3 mb-1">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">SciSkill Index</span>
        <span className="font-display text-2xl font-black tracking-tight text-rose-700">{body.sciskill_index?.toFixed(1)}</span>
        <span className={`pm-pill text-[10px] ${body.trend === "improving" ? "pm-pill-green" : body.trend === "declining" ? "pm-pill-red" : "pm-pill-slate"}`}>
          {body.trend === "improving" ? "↑ Improving" : body.trend === "declining" ? "↓ Declining" : "→ Stable"}
        </span>
      </div>
      <div className="space-y-1">
        {(body.radar || []).map((r) => (
          <div key={r.attribute} className="flex items-center gap-2 text-[11px]">
            <span className="w-20 text-slate-700 truncate">{r.attribute}</span>
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${PERC_TONE[r.tone] || "bg-slate-400"}`} style={{ width: `${r.percentile}%` }} />
            </div>
            <span className="w-8 text-right font-mono-tab font-bold">{r.percentile}th</span>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-slate-500 italic">{body.role}</div>
      {body.stub && <span className="pm-pill pm-pill-amber text-[10px]">Stub data · connect real SciSports for live</span>}
    </div>
  );
}

function TransferRoomBody({ body }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">xTV</div>
          <div className="font-display text-2xl font-black tracking-tight">{fmtMoney(body.xtv_eur)}</div>
        </div>
        <div className="flex-1" />
        <span className={`pm-pill text-[10px] ${body.availability === "Available" ? "pm-pill-green" : "pm-pill-slate"}`}>{body.availability}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div><span className="text-slate-400 font-bold uppercase tracking-[0.16em] text-[9px]">Ask price</span><div className="font-bold">{fmtMoney(body.ask_price_eur)}</div></div>
        <div><span className="text-slate-400 font-bold uppercase tracking-[0.16em] text-[9px]">Salary range</span><div className="font-bold text-[11px]">{body.salary_benchmark}</div></div>
      </div>
      {body.club_requirement && (
        <div className="text-[11px] text-emerald-700 font-bold border-t border-slate-100 pt-2">⚑ {body.club_requirement}</div>
      )}
      {body.stub && <span className="pm-pill pm-pill-amber text-[10px]">Stub data</span>}
    </div>
  );
}

function ApiFootballBody({ body }) {
  const next = body.next_fixture;
  const last = body.last_result;
  if (body.account_error) {
    return (
      <div className="space-y-2" data-testid="api-football-account-error">
        <div className="text-[12px] text-red-700 font-bold flex items-center gap-1.5">
          <AlertCircle size={12} /> API-Football account issue
        </div>
        <div className="text-[11px] text-slate-600">{body.account_error}</div>
        <a href="https://dashboard.api-football.com" target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-900"
           data-testid="api-football-resolve-link">
          Open API-Football dashboard <ExternalLink size={10} />
        </a>
      </div>
    );
  }
  if (body.needs_sync) {
    return (
      <div className="text-[12px] text-slate-600" data-testid="api-football-needs-sync">
        No fixtures synced yet for this club. Click <b>Sync fixtures now</b> below to pull from API-Football.
      </div>
    );
  }
  if (!next && !last) return <div className="text-sm text-slate-500">No fixtures on file for this club.</div>;
  return (
    <div className="space-y-2">
      {next && (
        <div className="space-y-1">
          <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-slate-400">
            {next.is_past ? "Most recent fixture" : "Next match"}
          </div>
          <div className="font-bold text-sm">{next.home_team} <span className="text-slate-400 font-normal">vs</span> {next.away_team}</div>
          <div className="text-[11px] text-slate-600">{next.competition}{next.matchday ? ` · Matchday ${next.matchday}` : ""}{next.venue ? ` · ${next.venue}` : ""}</div>
          <div className="text-[11px] font-mono-tab text-slate-700">{next.kickoff && new Date(next.kickoff).toLocaleString("en-GB", {weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"})}</div>
        </div>
      )}
      {last && (
        <div className="border-t border-slate-100 pt-2 space-y-0.5">
          <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-slate-400">Last result</div>
          <div className="text-[12px] font-bold">{last.home_team} <span className="text-emerald-700">{last.score}</span> {last.away_team}</div>
          <div className="text-[11px] text-slate-600">{last.player_minutes}' · {last.player_goals}G · {last.player_assists}A</div>
        </div>
      )}
      {body.stub && <span className="pm-pill pm-pill-amber text-[10px]">Stub fixtures</span>}
    </div>
  );
}

function ContractBody({ body }) {
  const tone = body.urgency === "urgent" ? "red" : body.urgency === "warning" ? "amber" : "emerald";
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2 flex-wrap">
        <div className="font-bold text-sm">{body.club} — {body.type_label}</div>
        <span className="pm-pill pm-pill-green text-[10px]">{body.status}</span>
      </div>
      <div className="flex items-center gap-2 text-[12px]">
        {body.wage_eur ? <span><b>{fmtMoney(body.wage_eur)}</b>/{body.wage_period === "weekly" ? "wk" : "yr"} gross</span> : null}
        {body.agent_commission_pct ? <span className="text-slate-600">· {body.agent_commission_pct}% commission</span> : null}
      </div>
      {body.end_date && (
        <div className={`text-[12px] text-${tone}-700 font-bold`}>
          Expires: <span data-testid="contract-expiry">{body.end_date}</span>
          {body.days_to_end !== null && <> · {body.days_to_end}d remaining</>}
        </div>
      )}
      {body.progress_pct !== null && (
        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full bg-${tone}-500`} style={{ width: `${body.progress_pct}%` }} />
        </div>
      )}
      {body.urgency === "warning" && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 text-[11px] text-amber-800 font-bold">
          ⚠ Renewal action needed — contract expires in {body.days_to_end} days
        </div>
      )}
      {body.urgency === "urgent" && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5 text-[11px] text-red-800 font-bold">
          ⚠ Contract expires in {body.days_to_end} days — urgent
        </div>
      )}
    </div>
  );
}

function LegalBody({ body }) {
  if (!body.cases?.length) {
    return <div className="text-sm text-slate-500 italic">No active legal cases for this client.</div>;
  }
  return (
    <div className="space-y-2">
      {body.cases.map((c, i) => (
        <div key={i} className="text-[12px] border-l-2 border-orange-300 pl-2.5" data-testid={`legal-case-${i}`}>
          <div className="font-bold">{c.ref || c.id} <span className="text-slate-400 font-normal">·</span> {c.status} <span className="text-slate-400 font-normal">·</span> {c.title}</div>
          {c.next_deadline && <div className="text-[11px] text-slate-600">Next deadline: <span className="font-bold">{c.next_deadline}</span>{c.counsel ? ` · ${c.counsel}` : ""}</div>}
        </div>
      ))}
    </div>
  );
}

function InboxBody({ body, channel }) {
  if (!body.messages?.length) {
    return <div className="text-sm text-slate-500 italic">No messages from this client yet.</div>;
  }
  return (
    <div className="space-y-2">
      {body.messages.map((m) => (
        <Link key={m.id} to={`/inbox?thread=${m.thread_id}`}
              className="block py-1.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-1 px-1 rounded transition-colors"
              data-testid={`wedge-${channel}-msg-${m.id}`}>
          <div className="flex items-baseline gap-1.5">
            <span className="font-bold text-[12px] truncate">{m.from_name}</span>
            {m.category && m.category !== "unknown" && <span className="pm-pill pm-pill-slate text-[9px]">{m.category}</span>}
            <span className="flex-1" />
            {!m.is_read && m.direction === "inbound" && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
            {m.is_demo && <span className="pm-pill pm-pill-amber text-[9px]">Demo</span>}
          </div>
          {m.subject && <div className="text-[11px] font-bold text-slate-800 truncate">{m.subject}</div>}
          <div className="text-[11px] text-slate-600 truncate">{m.preview}</div>
        </Link>
      ))}
    </div>
  );
}

export default Wedge;
